import { inject, Injectable } from '@angular/core';
import { _, TranslateService } from '@ngx-translate/core';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { MenuItem } from 'primeng/api';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { ldContextMenuTodo } from '@app/main/leak-detection/context-menus/utils/ld-context-menu-todo';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';
import { LeakDetectionSoundSignalService } from '@app/main/leak-detection/services/leak-detection-sound-signal/leak-detection-sound-signal.service';

@Injectable({ providedIn: 'root' })
export class LdContextMenuLoggerService {
  private readonly translateService = inject(TranslateService);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);
  private readonly soundSignalService = inject(LeakDetectionSoundSignalService);

  public getMapItems(
    logger: ILdLogger,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    return this.getCommonItems(logger, LdActionPlace.Map, dispatch, viewMeasurementSystem);
  }

  public getTableItems(
    logger: ILdLogger,
    cell: IMainTableCellContext | undefined,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    return [
      ...this.contextMenuCopy.getCopyCellItems(cell),
      ...this.getCommonItems(logger, LdActionPlace.TabsPanel, dispatch, viewMeasurementSystem),
    ];
  }

  private getCommonItems(
    logger: ILdLogger,
    place: LdActionPlace,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    const { deployment, measurement } = logger;
    const nearbyEvents = this.contextMenuEvents.getNearbyEvents(deployment.coordinates);
    const relatedEvent = this.contextMenuEvents.getEventByLoggerDeploymentId(deployment.id);

    return [
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-logger-noise-details__label`)),
        icon: 'icons/icon-chart.svg',
        command: () => dispatch({ type: LdActionType.LoggerDbClick, place, item: logger }),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file__label`)),
        icon: 'icons/icon-noise.svg',
        disabled: !measurement?.soundSignalUrl,
        command: () => this.soundSignalService.downloadByMeasurement(measurement),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-manual-correlation__label`)),
        icon: 'icons/icon-drop.svg',
        command: () => ldContextMenuTodo('Manual correlation', logger),
      },
      { separator: true },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-assign-to-pipe-node__label`)),
        icon: 'icons/icon-assign-to-pipe-node.svg',
        disabled: false,
        // use disable for that
        iconStyle: { 'original-icon': true },
        command: () => ldContextMenuTodo('Assign to pipe node', logger),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-detach-from-pipe-node__label`)),
        icon: 'icons/icon-detach-from-pipe-node.svg',
        disabled: false,
        // use disable for that
        iconStyle: { 'original-icon': true },
        command: () => ldContextMenuTodo('Detach from pipe node', logger),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-create-pipe__label`)),
        icon: 'icons/icon-create-pipe.svg',
        command: () => ldContextMenuTodo('Create pipe', logger),
      },
      { separator: true },
      {
        ...this.contextMenuEvents.getCreateEventConfig(),
        command: () => ldContextMenuTodo('Create event', logger),
      },
      this.contextMenuEvents.getEventDetailsMenuItem(nearbyEvents, viewMeasurementSystem, event =>
        dispatch({ type: LdActionType.EventDbClick, place, item: event })
      ),
      {
        ...this.contextMenuEvents.getAddToExistingEventConfig(),
        command: () => ldContextMenuTodo('Add to existing event', logger),
      },
      {
        ...this.contextMenuEvents.getRemoveFromEventConfig(relatedEvent),
        command: () => ldContextMenuTodo('Remove from event', relatedEvent),
      },
      { separator: true },
      {
        label: this.translateService.instant(
          _(`@@leak-detection-context-menu-sound-signal-recording-start-times__label`)
        ),
        icon: 'icons/icon-asset-identifier.svg',
        command: () => ldContextMenuTodo('Sound signal recording start times', logger),
      },
      {
        label: this.translateService.instant(_(`@@context-menu-show-photos__label`)),
        icon: 'icons/icon-picture.svg',
        command: () => dispatch({ type: LdActionType.ShowPhotos, place, item: logger }),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
        icon: 'icons/icon-show-on-street.svg',
        iconStyle: { 'original-icon': true },
        command: () => dispatch({ type: LdActionType.ShowInStreetView, place, position: deployment.coordinates }),
      },
    ];
  }
}
